import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, Download, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";

export const TemplateFill = ({ missingIds = [] }) => {
  const [emps, setEmps] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [q, setQ] = useState("");
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warns, setWarns] = useState([]);

  useEffect(() => { api.listEmployees().then(setEmps).catch(() => toast.error("تعذّر تحميل الموظفين")); }, []);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? emps.filter((e) => `${e.name} ${e.iqama_number} ${e.employee_number || ""}`.toLowerCase().includes(s)) : emps;
  }, [emps, q]);
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };

  const fill = async () => {
    if (!tpl) return toast.error("ارفع نموذج شركة التأمين أولاً");
    if (!sel.size) return toast.error("اختر موظف واحد على الأقل");
    setBusy(true); setWarns([]);
    try {
      const fd = new FormData(); fd.append("file", tpl); fd.append("emp_ids", JSON.stringify([...sel]));
      const r = await api.insuranceSheetFill(fd);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${tpl.name.replace(/\.xlsx$/i, "")}_معبأ.xlsx`; a.click();
      URL.revokeObjectURL(url);
      try { setWarns(JSON.parse(r.headers["x-warnings"] || "[]")); } catch { setWarns([]); }
      toast.success(`تمت تعبئة النموذج بـ ${sel.size} موظف`);
    } catch (err) {
      let msg = "تعذّر تعبئة النموذج";
      try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="template-fill">
      <p className="text-xs text-slate-500">ارفع نموذج الإضافة الفاضي من شركة التأمين (xlsx) — الذكاء الاصطناعي يقرأ أعمدته وقوائمه المنسدلة ويعبّيه بصيغة التواريخ والقيم اللي تطلبها الشركة بالضبط.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".xlsx" id="ins-tpl-file" className="hidden" onChange={(e) => { setTpl(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="template-file-input" />
        <Button variant="outline" onClick={() => document.getElementById("ins-tpl-file").click()} className="gap-1.5" data-testid="template-upload-btn">
          <FileSpreadsheet className="h-4 w-4" /> {tpl ? tpl.name : "رفع نموذج الشركة"}
        </Button>
        {missingIds.length > 0 && <Button variant="outline" size="sm" onClick={() => setSel(new Set(missingIds))} data-testid="template-select-missing">اختيار غير المؤمَّنين ({missingIds.length})</Button>}
        <Button variant="outline" size="sm" onClick={() => setSel(new Set(list.map((e) => e.id)))} data-testid="template-select-all">تحديد الظاهرين</Button>
        <Button variant="outline" size="sm" onClick={() => setSel(new Set())} data-testid="template-select-none">إلغاء</Button>
      </div>
      <div className="relative"><Search className="absolute end-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو الإقامة" data-testid="template-search" /></div>
      <div className="max-h-[38vh] overflow-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
        {list.map((e) => (
          <label key={e.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`template-emp-${e.id}`}>
            <input type="checkbox" checked={sel.has(e.id)} onChange={() => toggle(e.id)} />
            <span className="font-medium text-slate-800">{e.name}</span>
            <span className="font-mono-nums text-xs text-slate-400" dir="ltr">{e.iqama_number}</span>
            {(!e.birth_date || !e.gender || !e.marital_status) && <span className="ms-auto rounded-full bg-amber-50 px-2 text-[10px] text-amber-700">بيانات ناقصة</span>}
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500" data-testid="template-selected-count">المحدد: {sel.size}</span>
        <Button disabled={busy} onClick={fill} className="ms-auto gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="template-fill-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تعبئة وتنزيل
        </Button>
      </div>
      {warns.length > 0 && <div className="rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="template-warnings">
        <b>انتبه — بيانات ناقصة قد تسبب رفض:</b>
        <ul className="mt-1 space-y-0.5">{warns.map((w, i) => <li key={i}>{w.name}: {w.missing.join("، ")}</li>)}</ul>
      </div>}
    </div>
  );
};
